import React from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const PaySettingsSection = ({ 
  formData, 
  handleInputChange, 
  errors,
  isEditing,
  setIsEditing,
  payRates,  // Auto-looked up from pay_scales
  onSave,
  isSaving
}) => {
  const perDiemTypeOptions = [
    { value: 'domestic', label: 'Domestic ($2.75/hr)' },
    { value: 'international', label: 'International ($2.75/hr)' }
  ];

  // Calculate years of service for display
  const getYearsOfService = () => {
    if (!formData?.hireDate) return null;
    const hire = new Date(formData.hireDate);
    const now = new Date();
    return Math.floor((now - hire) / (365.25 * 24 * 60 * 60 * 1000)) + 1;
  };

  const yearsOfService = getYearsOfService();

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-6 md:mb-8">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-xl bg-accent-blue/20 glow-subtle">
            <Icon name="DollarSign" size={20} color="var(--color-accent-blue)" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-foreground">Pay Settings</h2>
            <p className="text-xs md:text-sm text-muted-foreground font-caption">
              Contract rates auto-loaded from pay scales
            </p>
          </div>
        </div>
        <button
          onClick={() => setIsEditing(!isEditing)}
          className="flex items-center gap-2 px-3 py-2 md:px-4 md:py-2 rounded-lg bg-tertiary border border-accent-blue/20 text-accent-blue text-sm font-medium transition-smooth hover:border-accent-blue/60 hover:glow-subtle"
          aria-label={isEditing ? "Cancel editing" : "Edit pay settings"}
        >
          <Icon name={isEditing ? "X" : "Edit"} size={16} />
          <span className="hidden sm:inline">{isEditing ? 'Cancel' : 'Edit'}</span>
        </button>
      </div>

      {/* Auto-calculated pay rate display */}
      {payRates && (
        <div className="mb-6 p-4 rounded-lg bg-accent-green/10 border border-accent-green/20">
          <div className="flex items-start gap-3">
            <Icon name="CheckCircle" size={20} color="var(--color-accent-green)" className="flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground mb-2">Current Pay Rate (Auto-Calculated)</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Hourly Rate:</span>
                  <span className="ml-2 font-data text-accent-green">${payRates.hourlyRate?.toFixed(2)}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Pay Year:</span>
                  <span className="ml-2 font-data">{payRates.payYear}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Effective:</span>
                  <span className="ml-2 font-data">{payRates.effectiveDate}</span>
                </div>
                {yearsOfService && (
                  <div>
                    <span className="text-muted-foreground">Years of Service:</span>
                    <span className="ml-2 font-data">{yearsOfService}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {!payRates && formData?.rank && formData?.aircraftType && formData?.hireDate && (
        <div className="mb-6 p-4 rounded-lg bg-accent-amber/10 border border-accent-amber/20">
          <div className="flex items-start gap-3">
            <Icon name="AlertTriangle" size={20} color="var(--color-accent-amber)" className="flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground mb-1">Pay Rate Not Found</p>
              <p className="text-xs text-muted-foreground">
                Could not find pay rate for {formData.rank} on {formData.aircraftType}. 
                You can enter a manual override below.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        {/* Manual override - only if needed */}
        <Input
          label="Hourly Rate Override"
          type="number"
          name="hourlyRate"
          value={formData?.hourlyRate || ''}
          onChange={handleInputChange}
          placeholder={payRates ? `Auto: $${payRates.hourlyRate?.toFixed(2)}` : "Enter rate"}
          disabled={!isEditing}
          min="0"
          step="0.01"
          className="w-full font-data"
        />

        <Input
          label="Minimum Guarantee (Hours)"
          type="number"
          name="minimumGuarantee"
          value={formData?.minimumGuarantee || ''}
          onChange={handleInputChange}
          placeholder="0 (optional)"
          disabled={!isEditing}
          min="0"
          step="1"
          className="w-full font-data"
        />

        <Select
          label="Per Diem Type"
          options={perDiemTypeOptions}
          value={formData?.perDiemType || 'domestic'}
          onChange={(value) => handleInputChange({ target: { name: 'perDiemType', value } })}
          placeholder="Select per diem type"
          disabled={!isEditing}
          className="w-full"
        />

        <Input
          label="Per Diem Rate (Hourly)"
          type="number"
          name="perDiemRate"
          value={formData?.perDiemRate || ''}
          onChange={handleInputChange}
          placeholder="2.75 (optional)"
          disabled={!isEditing}
          min="0"
          step="0.01"
          className="w-full font-data"
        />

        <Input
          label="Premium Pay Multiplier"
          type="number"
          name="premiumMultiplier"
          value={formData?.premiumMultiplier || ''}
          onChange={handleInputChange}
          placeholder="1.5 (optional)"
          disabled={!isEditing}
          min="1"
          step="0.1"
          className="w-full font-data"
        />

        <Input
          label="Override Rate"
          type="number"
          name="overrideRate"
          value={formData?.overrideRate || ''}
          onChange={handleInputChange}
          placeholder="0.00 (optional)"
          disabled={!isEditing}
          min="0"
          step="0.01"
          className="w-full font-data"
        />

        <Input
          label="Night Differential Rate"
          type="number"
          name="nightDifferential"
          value={formData?.nightDifferential || ''}
          onChange={handleInputChange}
          placeholder="0.00 (optional)"
          disabled={!isEditing}
          min="0"
          step="0.01"
          className="w-full font-data"
        />

        <Input
          label="Sick Time Accrued (Hours)"
          type="number"
          name="sickTimeAccrued"
          value={formData?.sickTimeAccrued || ''}
          onChange={handleInputChange}
          placeholder="0 (optional)"
          disabled={!isEditing}
          min="0"
          step="0.5"
          className="w-full font-data"
        />
      </div>

      <div className="mt-6 p-4 rounded-lg bg-accent-blue/10 border border-accent-blue/20">
        <div className="flex items-start gap-3">
          <Icon name="Calculator" size={20} color="var(--color-accent-blue)" className="flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-foreground mb-2">Pay Calculation</p>
            <p className="text-xs md:text-sm text-muted-foreground">
              Your hourly rate is automatically determined from the pay scales based on your position, 
              aircraft type, and years of service. Override fields are optional and only used when 
              the auto-calculation doesn't match your specific situation.
            </p>
          </div>
        </div>
      </div>

      {isEditing && (
        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={() => setIsEditing(false)}
            className="px-4 py-2 rounded-lg bg-tertiary border border-border text-muted-foreground text-sm font-medium transition-smooth hover:bg-secondary"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            disabled={isSaving}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-accent-blue text-background text-sm font-medium transition-smooth hover:bg-accent-blue/90 disabled:opacity-50"
          >
            <Icon name="Save" size={16} />
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      )}
    </div>
  );
};

export default PaySettingsSection;